(ns manage.adset-decision-tree-page.view.side-panel.property-form
  (:require ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->js ->clj]]
            [clojure.string :as str]
            [common.form :as form]
            [malli.core :as m]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.property :as property]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.validation :as validation]
            [manage.adset-decision-tree-page.view.context :as context]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defn get-property-field-name
  [{:keys [data-path]} & field-names]
  (->> field-names
       (apply conj (vec data-path))
       (map (fn [field-key] (if (keyword? field-key)
                              (name field-key)
                              (str field-key))))
       (str/join ".")))

(defhook use-set-value-for-field-name [{:keys [resetField]} field-name]
  (uix/use-callback (fn [new-value] (resetField field-name #js {:defaultValue new-value}))
                    [resetField field-name]))

(defhook use-set-property-value [form-control property]
  (use-set-value-for-field-name form-control (get-property-field-name property)))

(defhook use-property-value [{:keys [control]} property]
  (rhf/useWatch #js {:name (get-property-field-name property)
                     :control control}))

(defn get-initial-form-value
  [selected-item selected-node properties properties-context]
  (let [base-path (selection/get-selected-item-base-path selected-node selected-item)
        base-data (cond-> selected-node
                    (some? base-path) (get-in base-path))]
    (->> properties
         (map (comp first :data-path))
         (select-keys base-data)
         (property/->form properties properties-context)
         ->js)))

(defn get-new-property-value
  [{:keys [data-path context] {convert-fn :from} :form-convert}
   properties-context
   new-form-data]
  (let [;; use js->clj here to avoid injecting mutable js object into reframe db
        new-property-value (js->clj (->js (get-in new-form-data data-path))
                                    :keywordize-keys true)
        property-context (get properties-context context)]
    (cond-> new-property-value
      (some? convert-fn) (convert-fn property-context))))

(defn validate-property-value
  [{:keys [data-path schema] {:keys [to-validation from]} :form-convert}
   context
   new-form-data]
  (let [convert-fn (or to-validation from)]
    (m/validate schema (cond-> (get-in new-form-data data-path)
                         (some? convert-fn) (convert-fn context)))))

(defn get-save-patch-data
  [{:keys [selected-node property new-form-data properties-context base-path]}]
  (let [data-path (into (vec base-path) (:data-path property))
        patch-key (first data-path)
        converted-new-value (get-new-property-value property properties-context new-form-data)
        existing-value (get-in selected-node data-path)
        updated-node
        (if (and (:dissoc-on-nil property) (nil? converted-new-value))
          (update-in selected-node (butlast data-path) dissoc (last data-path))
          (assoc-in selected-node data-path converted-new-value))]
    (when (not= existing-value converted-new-value)
      {:node-id (:id selected-node)
       :patch-key patch-key
       :patch-data (get updated-node patch-key)})))

(defn handle-be-save [selected-node
                      base-path
                      properties-with-field-path
                      properties-context
                      {changed-field-name :name
                       new-form-data :values}]
  (let [property (some (fn [{:keys [field-name data-path] :as property}]
                         (when (and (seq data-path)
                                    (str/starts-with? changed-field-name field-name))
                           property))
                       properties-with-field-path)
        save-patch-data (get-save-patch-data {:selected-node selected-node
                                              :property property
                                              :new-form-data new-form-data
                                              :properties-context properties-context
                                              :base-path base-path})]
    (when (and (some? save-patch-data)
               (not (:disabled property))
               (or (not (:do-not-patch-invalid property))
                   (validate-property-value property properties-context new-form-data)))
      (rf/dispatch [::event/handle-property-form-save save-patch-data]))))

(defhook use-property-resolver [properties]
  (let [{:keys [properties-context]} (uix/use-context context/decision-trees-context)
        convert-fn (uix/use-callback
                    (fn [data] (property/form->validation properties properties-context data))
                    [properties properties-context])
        schema (uix/use-memo
                #(validation/get-schema-for-properties properties properties-context)
                [properties properties-context])
        resolver (form/use-malli-resolver schema)]
    (uix/use-callback
     (fn [data] (resolver (convert-fn (->clj data))))
     [resolver convert-fn])))

(defhook use-property-form
  [{:keys [initial-form-value properties disabled]}]
  (form/use-form #js {:mode "all"
                      :disabled disabled
                      :values initial-form-value
                      :resolver (use-property-resolver properties)}))

(defhook use-selected-node-property-form
  [{:keys [selected-item selected-node properties]
    {:keys [form-disabled-predicate]} :selected-node}]
  (let [new-node-selected (use-subscribe [::sub/new-node-selected])
        {:keys [properties-context]} (uix/use-context context/decision-trees-context)

        form-value
        (uix/use-memo
         #(get-initial-form-value selected-item selected-node properties properties-context)
         [selected-item selected-node properties properties-context])

        form-value-ref (uix/use-ref form-value)

        initial-form-value
        (uix/use-memo
         ;; don't refresh initial value on every node change (save) for autosave nodes
         #(if (:disable-side-panel-autosave selected-node) form-value @form-value-ref)
         [form-value selected-node])

        disabled (boolean (or (:disabled-form selected-item)
                              (and (some? form-disabled-predicate)
                                   (form-disabled-predicate
                                    (->clj initial-form-value)))))
        {:keys [trigger reset] :as form-control} (use-property-form
                                                  {:initial-form-value initial-form-value
                                                   :properties properties
                                                   :disabled disabled})

        base-path (uix/use-memo
                   #(selection/get-selected-item-base-path selected-node selected-item)
                   [selected-node selected-item])
        properties-with-field-path (uix/use-memo
                                    #(map (fn [property]
                                            (assoc property :field-name
                                                   (get-property-field-name property)))
                                          properties)
                                    [properties])
        handle-be-save
        (uix/use-callback
         (fn [_data details]
           (when-not (:disable-side-panel-autosave selected-node)
             (handle-be-save selected-node
                             base-path
                             properties-with-field-path
                             properties-context
                             details)))
         [selected-node base-path properties-with-field-path properties-context])

        [is-reseted set-reseted] (uix/use-state false)]
    ;; Trigger needs to happen after value reset
    (uix/use-effect (fn []
                      (reset initial-form-value)
                      (set-reseted true))
                    [reset initial-form-value])
    (uix/use-effect #(when (and (not new-node-selected) is-reseted)
                       (trigger))
                    [trigger new-node-selected is-reseted])
    (form/use-form-callback form-control handle-be-save {})
    form-control))

(defhook use-property-editor-props
  [{:keys [selected-item selected-node properties] :as props}]
  (let [form-control (use-selected-node-property-form props)]
    (for [property properties]
      {:key (:id property)
       :form-control form-control
       :selected-item selected-item
       :property property
       :selected-node selected-node})))

(defhook use-props-with-property-field-array
  [{:keys [form-control property] :as props}]
  (assoc props :field-array (form/use-field-array
                             #js {:control (:control form-control)
                                  :name (get-property-field-name property)})))
